import React, { useMemo } from "react";
import { useLanguage } from "../i18n/LanguageContext";

/* Generative Quran Tree — every memorized ayah adds a leaf, every page a branch. */
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const LEAF_COLORS = ["#3f6b4f", "#5a8a67", "#77a884", "#c9a227", "#93b89a"];

export default function QuranTree({ ayahs = 0, pages = 0 }) {
  const { t: tr } = useLanguage();
  const model = useMemo(() => {
    const rand = mulberry32(1447);
    const branchCount = Math.max(1, Math.min(Math.floor(pages) + 1, 24));
    const growth = Math.min(1, Math.min(ayahs, 30) / 30 * 0.2 + Math.min(pages, 40) / 40 * 0.8);
    const trunkH = 46 + growth * 200;
    const trunkTop = 380 - trunkH;
    const renderLeaves = Math.min(ayahs, 320);

    const branches = [];
    for (let i = 0; i < branchCount; i++) {
      const t = (i + 1) / (branchCount + 1);
      const sy = 380 - trunkH * (0.35 + 0.62 * t);
      const dir = i % 2 === 0 ? 1 : -1;
      const angle = (25 + rand() * 40) * (Math.PI / 180);
      const len = (34 + rand() * 46) * growth + 18;
      branches.push({
        sx: 200, sy,
        ex: 200 + dir * len * Math.cos(angle),
        ey: sy - len * Math.sin(angle),
        w: Math.max(1.5, 5 * (1 - t) * growth + 1),
      });
    }

    const leaves = [];
    if (renderLeaves > 0) {
      const perBranch = Math.ceil(renderLeaves / branches.length);
      let placed = 0;
      for (const b of branches) {
        for (let j = 0; j < perBranch && placed < renderLeaves; j++, placed++) {
          leaves.push({
            x: b.ex + (rand() - 0.5) * 44,
            y: b.ey + (rand() - 0.5) * 36 - 6,
            r: 4 + rand() * 4,
            rot: rand() * 360,
            c: LEAF_COLORS[Math.floor(rand() * LEAF_COLORS.length)],
            o: 0.7 + rand() * 0.3,
          });
        }
      }
    }
    return { trunkH, trunkTop, branches, leaves };
  }, [ayahs, pages]);

  const isSeed = ayahs === 0;
  const minY = isSeed ? 280 : Math.max(0, model.trunkTop - 110);

  return (
    <svg viewBox={`0 ${minY} 400 ${420 - minY}`} className="w-full max-w-md mx-auto" data-testid="quran-tree" role="img"
      aria-label={tr("quranTree.ariaLabel", { ayahs, branches: Math.floor(pages) })}>
      {/* ground */}
      <ellipse cx="200" cy="388" rx="130" ry="16" fill="#e6dcc6" />
      <ellipse cx="200" cy="384" rx="90" ry="10" fill="#d9cbab" />

      {isSeed ? (
        <g>
          <path d="M200 384 Q198 360 200 348" stroke="#5a8a67" strokeWidth="3" fill="none" strokeLinecap="round" />
          <ellipse cx="192" cy="348" rx="7" ry="11" fill="#5a8a67" transform="rotate(-35 192 348)" />
          <ellipse cx="208" cy="346" rx="7" ry="11" fill="#77a884" transform="rotate(35 208 346)" />
        </g>
      ) : (
        <g>
          {/* trunk */}
          <path
            d={`M192 384 C 194 ${384 - model.trunkH * 0.4}, 196 ${384 - model.trunkH * 0.7}, 200 ${model.trunkTop}
                C 204 ${384 - model.trunkH * 0.7}, 206 ${384 - model.trunkH * 0.4}, 208 384 Z`}
            fill="#6b4f35"
          />
          {/* branches */}
          {model.branches.map((b, i) => (
            <path key={i} d={`M${b.sx} ${b.sy} Q ${(b.sx + b.ex) / 2} ${b.ey - 6} ${b.ex} ${b.ey}`}
              stroke="#6b4f35" strokeWidth={b.w} fill="none" strokeLinecap="round" />
          ))}
          {/* soft canopy */}
          {model.leaves.length > 30 && model.branches.map((b, i) => (
            <circle key={`c${i}`} cx={b.ex} cy={b.ey - 4} r={26} fill="#5a8a67" opacity="0.12" />
          ))}
          {/* leaves */}
          {model.leaves.map((l, i) => (
            <ellipse key={i} cx={l.x} cy={l.y} rx={l.r * 0.6} ry={l.r} fill={l.c} opacity={l.o}
              transform={`rotate(${l.rot} ${l.x} ${l.y})`} />
          ))}
        </g>
      )}
    </svg>
  );
}
